/**
 * <tcr-coverflow> — carrusel infinito con foco en el centro.
 * ---------------------------------------------------------------------------
 * Cinco tarjetas a la vista: la central a tamano completo, las vecinas un
 * escalon mas pequenas y las de las orillas otro escalon mas. Gira sola y en
 * bucle: despues de la ultima vuelve la primera, sin salto ni rebote.
 *
 * POR QUE NO USA scroll-snap (que es lo que hacia la version anterior)
 * Un contenedor con scroll tiene principio y final. Para dar la vuelta hay que
 * saltar el scroll de un extremo al otro, y ese salto se ve — o se clona el
 * contenido, que duplica nodos y ensucia la accesibilidad. Aqui la posicion de
 * cada tarjeta se calcula como su DISTANCIA al indice activo en aritmetica
 * modular, asi que el bucle es una propiedad de la formula y no un caso
 * especial: la tarjeta 0 esta a distancia 1 de la ultima igual que de la 1.
 *
 * COMO SE COLOCA CADA TARJETA
 *   d = distancia con signo al centro, envuelta al rango [-N/2, N/2]
 *   x = d * (ancho de tarjeta * separacion)
 *   s = escalonEscala   ^ |d|     (1 -> 0,85 -> 0,72 con escalon 0,85)
 *   o = escalonOpacidad ^ |d|
 *   z = vecinas - |d|             (la central siempre por encima)
 * Mas alla del numero de vecinas visibles la tarjeta se apaga. No se destruye:
 * solo deja de pintarse.
 *
 * CUANTAS CABEN
 * Nunca se muestran mas tarjetas que bloques haya, o la misma resena saldria
 * dos veces a la vez. Las vecinas se recortan a floor((N-1)/2): con 5 resenas
 * o mas salen las cinco posiciones; con 3 o 4 salen tres.
 *
 * MOVIL
 * Ni giro automatico ni tres escalones: manda el dedo. Una vecina a cada lado,
 * arrastre con seguimiento en vivo y salto al soltar. Rotar solo en un movil
 * es hostil: el usuario esta leyendo y el contenido se le escapa.
 *
 * ACCESIBILIDAD
 * · `prefers-reduced-motion` apaga el giro y las transiciones.
 * · Pausa al pasar el raton, al enfocar con teclado, al arrastrar y al salir
 *   de pantalla.
 * · Flechas del teclado para moverse; los puntos son botones de verdad. Que
 *   exista SIEMPRE un control manual no es decorativo: contenido que se mueve
 *   solo mas de cinco segundos sin forma de pararlo incumple la WCAG 2.2.2.
 * · Lo que no se ve lleva `inert`, asi que el tabulador no cae en una resena
 *   invisible.
 *
 * EDITOR DE TEMAS
 * Solo con `Shopify.designMode`: al seleccionar un bloque en el panel, el
 * carrusel lo lleva al centro y deja de girar mientras siga seleccionado.
 *
 * Sin librerias. No toca nada fuera de su propio arbol (los unicos listeners
 * en `document` son los del editor y `visibilitychange`, y se retiran al
 * desmontar). Se carga como modulo: sus constantes no son globales de pagina.
 */
const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');
// Dos cortes distintos, y no es lo mismo:
// COMPACT (750) decide el COMPORTAMIENTO — en movil no gira sola, manda el dedo.
// NARROW (1150) decide CUANTAS caben. Entre 750 y 1150 el movil se comporta
// como escritorio pero no hay ancho para cinco: cinco tarjetas a 820 px se
// salian 133 px por cada lado. Ahi bajan a tres.
const COMPACT = matchMedia('(max-width: 749px)');
const NARROW = matchMedia('(max-width: 1149px)');

class TcrCoverflow extends HTMLElement {
  /** @type {AbortController | null} */
  abortController = null;

  connectedCallback() {
    this.track = this.querySelector('[data-cf-track]');
    if (!this.track) return;

    this.slides = Array.from(this.track.children);
    this.count = this.slides.length;
    if (this.count < 2) return;

    this.dots = Array.from(this.querySelectorAll('[data-cf-dot]'));

    this.autoplayMs = parseInt(this.dataset.autoplay || '0', 10);
    this.scaleStep = parseFloat(this.dataset.scaleStep || '0.85');
    this.opacityStep = parseFloat(this.dataset.opacityStep || '0.62');
    this.spread = parseFloat(this.dataset.spread || '0.95');
    // Hueco visible entre tarjetas: el `gap` del carril en CSS (una sola
    // fuente para diseño y JS), modulado por el ajuste `spread` del editor.
    this.gapPx = (parseFloat(getComputedStyle(this.track).columnGap) || 16) * this.spread;

    this.index = 0;
    this.drag = null;
    this.dragOffset = 0;
    this.held = false;
    this.visible = false;

    // Todos los listeners cuelgan de un AbortController que se aborta en
    // disconnectedCallback: en el editor cada re-render desmonta y vuelve a
    // montar el elemento, y sin esto cada instancia muerta seguia suscrita
    // a las media queries y podia arrancar un setInterval sobre un carrusel
    // que ya no existia.
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.onResize = this.onResize.bind(this);
    this.onReduced = () => this.syncTimer();
    this.onVisibility = () => this.syncTimer();

    this.track.addEventListener('pointerdown', this.onDown.bind(this), { signal });
    this.track.addEventListener('pointermove', this.onMove.bind(this), { signal });
    this.track.addEventListener('pointerup', this.onUp.bind(this), { signal });
    this.track.addEventListener('pointercancel', this.onUp.bind(this), { signal });
    this.addEventListener('keydown', this.onKey.bind(this), { signal });

    ['pointerenter', 'focusin'].forEach((e) => this.addEventListener(e, () => this.hold(true), { signal }));
    ['pointerleave', 'focusout'].forEach((e) =>
      this.addEventListener(
        e,
        () => {
          if (!this.contains(document.activeElement)) this.hold(false);
        },
        { signal }
      )
    );

    this.dots.forEach((dot, i) =>
      dot.addEventListener(
        'click',
        () => {
          this.index = i;
          this.layout();
        },
        { signal }
      )
    );

    window.addEventListener('resize', this.onResize, { passive: true, signal });
    COMPACT.addEventListener('change', this.onResize, { signal });
    NARROW.addEventListener('change', this.onResize, { signal });
    REDUCED.addEventListener('change', this.onReduced, { signal });
    // IntersectionObserver no se entera de que la pestana se ha ocultado:
    // el intervalo seguia girando (el navegador lo limita a 1/s). Con
    // `document.hidden` como condicion de apagado se para de verdad.
    document.addEventListener('visibilitychange', this.onVisibility, { signal });

    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(
        (entries) => {
          this.visible = entries[0].isIntersecting;
          this.syncTimer();
        },
        { threshold: 0.3 }
      );
      this.io.observe(this);
    } else {
      this.visible = true;
    }

    // EDITOR DE TEMAS. `theme-editor.js` de Horizon solo atiende a sus
    // propios carruseles; sin esto, seleccionar una resena en el panel
    // resaltaba una tarjeta con opacidad 0 e `inert`, y el cambio de texto
    // no se veia. Se lleva el carrusel al bloque y se deja quieto mientras
    // esta seleccionado.
    if (window.Shopify?.designMode) {
      this.onBlockSelect = (event) => {
        const slide = event.target instanceof Element ? event.target.closest('[data-cf-track] > *') : null;
        const i = slide ? this.slides.indexOf(slide) : -1;
        if (i === -1) return;
        this.index = i;
        this.held = true;
        this.layout();
        this.syncTimer();
      };
      this.onBlockDeselect = (event) => {
        const slide = event.target instanceof Element ? event.target.closest('[data-cf-track] > *') : null;
        if (!slide || !this.slides.includes(slide)) return;
        this.held = false;
        this.syncTimer();
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect, { signal });
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect, { signal });
    }

    // `is-init` mata la transicion durante la primera colocacion: si no, las
    // cinco tarjetas entran volando desde la pila inicial en cada carga. Dos
    // rAF porque uno solo no basta: el navegador tiene que haber pintado ya
    // el estado nuevo antes de que sea seguro reactivar la animacion.
    this.classList.add('is-init');
    this.setAttribute('data-ready', '');
    this.measure();
    this.layout();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.classList.remove('is-init'));
    });
    this.syncTimer();
  }

  disconnectedCallback() {
    // `visible` a false ANTES de nada: si un listener rezagado llamase a
    // syncTimer, no arrancaria un intervalo sobre un elemento desmontado.
    this.visible = false;
    this.abortController?.abort();
    this.abortController = null;
    this.io?.disconnect();
    this.io = null;
    clearInterval(this.timer);
    this.timer = null;
  }

  /** Vecinas a cada lado: por ancho disponible y sin repetir tarjeta nunca. */
  get neighbours() {
    const wanted = NARROW.matches ? 1 : 2;
    return Math.max(1, Math.min(wanted, Math.floor((this.count - 1) / 2)));
  }

  /**
   * La altura la fija el JS porque las tarjetas van en posicion absoluta y ya
   * no empujan al contenedor. Se toma la mas alta: las resenas tienen
   * longitudes distintas, y recortar la mas larga seria peor que dejar aire
   * debajo de las cortas.
   */
  measure() {
    this.cardW = this.slides[0].offsetWidth || 320;

    // Hay que soltar la altura ANTES de medir. El CSS le da a cada tarjeta
    // `height: 100%` para que todas midan igual, pero eso las ata a la altura
    // del contenedor, que es justo lo que estamos calculando. Medir sin
    // soltarla devolveria el valor anterior y la altura no creceria nunca.
    for (const slide of this.slides) slide.style.height = 'auto';

    let tallest = 0;
    for (const slide of this.slides) tallest = Math.max(tallest, slide.offsetHeight);

    for (const slide of this.slides) slide.style.height = '';

    if (tallest) this.style.setProperty('--tcr-cf-h', Math.ceil(tallest) + 'px');
  }

  onResize() {
    this.measure();
    this.layout();
    this.syncTimer();
  }

  /** Distancia con signo al centro, envuelta: aqui vive el bucle infinito. */
  offsetOf(i) {
    const n = this.count;
    let d = (i - this.index) % n;
    if (d > n / 2) d -= n;
    if (d < -n / 2) d += n;
    return d;
  }

  /**
   * Desplazamiento acumulado hasta la tarjeta a posiciones del centro.
   *
   * ANTES era `d * cardW * spread`: un paso CONSTANTE. Pero cada tarjeta se
   * encoge (scaleStep^a), asi que el hueco VISIBLE entre bordes crecia con
   * la distancia — las del centro casi pegadas y las de fuera muy sueltas.
   * Ahora se suma, tarjeta a tarjeta, la media de los dos anchos escalados
   * mas un hueco fijo: la separacion se ve igual en toda la tira. El hueco
   * sale del `gap` del carril en CSS (una sola fuente) y `spread` lo ajusta.
   */
  offsetX(a) {
    let x = 0;
    for (let k = 1; k <= a; k++) {
      const anterior = this.cardW * Math.pow(this.scaleStep, k - 1);
      const actual = this.cardW * Math.pow(this.scaleStep, k);
      x += (anterior + actual) / 2 + this.gapPx;
    }
    return x;
  }

  layout() {
    const near = this.neighbours;

    this.slides.forEach((slide, i) => {
      const d = this.offsetOf(i);
      const a = Math.abs(d);
      const shown = a <= near;
      const x = Math.sign(d) * this.offsetX(a) + this.dragOffset;

      slide.style.transform =
        'translate3d(calc(-50% + ' + x + 'px), -50%, 0) scale(' + Math.pow(this.scaleStep, a) + ')';
      slide.style.opacity = shown ? Math.pow(this.opacityStep, a) : 0;
      slide.style.zIndex = near - a + 1;
      // Las vecinas visibles se pueden pulsar para traerlas al centro (ver
      // onUp); solo las apagadas quedan fuera del alcance del puntero.
      slide.style.pointerEvents = shown ? 'auto' : 'none';
      slide.toggleAttribute('data-cf-active', a === 0);
      slide.toggleAttribute('inert', !shown);
    });

    this.dots.forEach((dot, i) => {
      const on = i === this.index;
      dot.toggleAttribute('data-cf-on', on);
      if (on) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  step(dir) {
    this.index = (this.index + dir + this.count) % this.count;
    this.layout();
  }

  onKey(e) {
    if (e.key === 'ArrowRight') {
      this.step(1);
      e.preventDefault();
    } else if (e.key === 'ArrowLeft') {
      this.step(-1);
      e.preventDefault();
    }
  }

  onDown(e) {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const slide = e.target instanceof Element ? e.target.closest('[data-cf-track] > *') : null;
    this.drag = { x: e.clientX, y: e.clientY, moved: false, slide };
    this.hold(true);
  }

  onMove(e) {
    if (!this.drag) return;
    const dx = e.clientX - this.drag.x;
    const dy = e.clientY - this.drag.y;
    // Hasta que el gesto no es claramente horizontal no se captura: si no,
    // arrastrar la pagina en vertical sobre el carrusel la dejaria clavada.
    if (!this.drag.moved) {
      if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy)) return;
      this.drag.moved = true;
      this.classList.add('is-dragging');
      if (this.track.setPointerCapture) this.track.setPointerCapture(e.pointerId);
    }
    this.dragOffset = dx;
    this.layout();
  }

  onUp() {
    if (!this.drag) return;
    const dx = this.dragOffset;
    const { moved, slide } = this.drag;
    this.dragOffset = 0;
    this.classList.remove('is-dragging');
    this.drag = null;

    if (Math.abs(dx) > this.cardW * 0.18) {
      this.step(dx < 0 ? 1 : -1);
    } else if (!moved && slide) {
      // Toque sin arrastre sobre una vecina: se trae al centro, que es el
      // gesto que todo el mundo espera de un coverflow.
      const i = this.slides.indexOf(slide);
      if (i !== -1 && i !== this.index) this.index = i;
      this.layout();
    } else {
      this.layout();
    }

    this.hold(false);
  }

  hold(state) {
    this.held = state;
    this.syncTimer();
  }

  syncTimer() {
    clearInterval(this.timer);
    this.timer = null;
    // En movil no gira sola: manda el dedo. Con la pestana oculta, tampoco.
    const off = REDUCED.matches || COMPACT.matches || !this.autoplayMs || document.hidden;
    if (off || this.held || !this.visible) return;
    this.timer = setInterval(() => this.step(1), this.autoplayMs);
  }
}

if (!customElements.get('tcr-coverflow')) {
  customElements.define('tcr-coverflow', TcrCoverflow);
}
